import React from 'react';
import { Link } from 'react-router-dom';
import { Post as PostData, Photo as PhotoData } from './HomePage'; // Assuming HomePage exports these types
import { ProgressiveImage } from './ProgressiveImage';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { DEFAULT_PROFILE_PICTURE } from '../constants/defaults';
import { User as FirebaseUser } from 'firebase/auth'; // Correct Firebase User import
import { useAuth } from '../contexts/AuthContext';
// It's good practice to import CSS specific to the component if styles diverge significantly
// Or rely on global styles if they are sufficient (like HomePage.css might be)
import './HomePage.css'; // Assuming HomePage.css contains needed styles for .post-card etc.

interface PostCardProps {
  post: PostData;
  onCardClick: (postId: string, e: React.MouseEvent) => void;
  onYumClick?: (postId: string) => void; // Make Yum optional, maybe not shown everywhere
  yumLoginErrorPostId?: string | null; // To show login error message
}

const PostCard: React.FC<PostCardProps> = ({ 
  post, 
  onCardClick, 
  onYumClick, 
  yumLoginErrorPostId
}) => {
  const { currentUser } = useAuth();
  
  const handleYumButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking button
    if (onYumClick) {
      onYumClick(post.id);
    }
  };

  // Use first_photo for the grid preview
  const previewPhoto = post.first_photo;

  return (
    <div 
      key={post.id} 
      className={`post-card`} 
      onClick={(e) => onCardClick(post.id, e)}
    >
      <div className="post-header flex justify-between items-start">
        <div className="user-info flex flex-1 items-center min-w-0 mr-2">
          <img 
            src={post.user_avatar_url || DEFAULT_PROFILE_PICTURE} 
            alt={post.user_display_name || 'User'} 
            className="user-avatar flex-shrink-0"
          />
          <div className="post-info ml-2 min-w-0 flex flex-col">
            <Link 
              to={post.user_id === currentUser?.uid ? '/profile' : `/profile/u/${post.user_name}`}
              className="user-name font-semibold text-base post-author-link"
              onClick={(e) => e.stopPropagation()}
            >
              {post.user_display_name}
            </Link>
            {post.restaurant_name && (
              <div className="restaurant-info flex items-center text-xs text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="location-icon flex-shrink-0 mr-0.5">
                  <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.757.433.36.36 0 00.28.14l.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clipRule="evenodd" />
                </svg>
                <span className="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">{post.restaurant_name}</span>
              </div>
            )}
          </div>
        </div>
        <span className="post-time text-xs text-gray-500 flex-shrink-0">
          {new Date(post.visit_date || post.created_at).toLocaleDateString()}
        </span>
      </div>
      
      {previewPhoto && (
        <div className="post-image-container">
          <ProgressiveImage
            src={`${API_BASE_URL}/api/posts/images/${previewPhoto.displayUrl}`}
            thumbnailSrc={`${API_BASE_URL}/api/posts/images/${previewPhoto.thumbnailUrl}`}
            alt={previewPhoto.name || post.title || 'Post image'}
            className="post-image"
          />
        </div>
      )}
      
      <div className="post-content">
        <h3 className="post-title">{post.title}</h3>
        <p className="caption">{post.content}</p>
      </div>
      
      <div className="post-divider" />
      
      {/* Actions - Conditionally render Yum button if handler is provided */}
      {onYumClick && (
        <div className="post-actions" onClick={(e) => e.stopPropagation()}> 
          <button 
            className="action-button yum-button"
            onClick={handleYumButtonClick}
            disabled={!currentUser || currentUser.uid === post.user_id}
            title={
              !currentUser 
                ? "Log in to Yum" 
                : currentUser.uid === post.user_id 
                ? "You cannot Yum your own post" 
                : "Yum this post!"
            }
          >
            <svg className={`w-5 h-5 ${post.is_yummed ? "text-red-500" : ""}`} fill={post.is_yummed ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>Yum! ({post.yum_count !== undefined ? post.yum_count : 0})</span>
          </button>
        </div>
      )}

      {/* Display login error message if applicable */}      
      {onYumClick && yumLoginErrorPostId === post.id && (
        <div className="text-red-600 text-xs mt-2 ml-1">
          Please log in to Yum!
        </div>
      )}
    </div>
  );
};

export default PostCard; 